import React from 'react';
import Slider from '../components/Slider';

const PAIN_LOCATIONS = [
  { key: 'pelvic', label: 'Pelvic' },
  { key: 'lower_back', label: 'Lower Back' },
  { key: 'bladder', label: 'Bladder' },
  { key: 'bowel', label: 'Bowel' },
  { key: 'hip', label: 'Hip' },
  { key: 'shoulder', label: 'Shoulders' },
];

const MOOD_OPTIONS = [
  { key: 'happy', label: 'Happy' },
  { key: 'calm', label: 'Calm' },
  { key: 'content', label: 'Content' },
  { key: 'hopeful', label: 'Hopeful' },
  { key: 'anxious', label: 'Anxious' },
  { key: 'tired', label: 'Tired' },
  { key: 'irritable', label: 'Irritable' },
  { key: 'sad', label: 'Sad' },
  { key: 'overwhelmed', label: 'Overwhelmed' },
  { key: 'frustrated', label: 'Frustrated' },
  { key: 'numb', label: 'Numb' },
  { key: 'weepy', label: 'Weepy' },
  { key: 'angry', label: 'Angry' },
];

const GI_OPTIONS = [
  { key: 'bloating', label: 'Bloating' },
  { key: 'nausea', label: 'Nausea' },
  { key: 'constipation', label: 'Constipation' },
  { key: 'diarrhea', label: 'Diarrhea' },
  { key: 'gas', label: 'Gas' },
  { key: 'acid_reflux', label: 'Acid Reflux' },
  { key: 'cramps', label: 'Stomach Cramps' },
];

export default function SymptomStep({ data, update }) {
  const toggleLocation = (loc) => {
    const current = data.painLocations || [];
    if (current.includes(loc)) {
      update({ painLocations: current.filter(l => l !== loc) });
    } else {
      update({ painLocations: [...current, loc] });
    }
  };

  const toggleMood = (mood) => {
    const current = data.moods || [];
    if (current.includes(mood)) {
      update({ moods: current.filter(m => m !== mood) });
    } else {
      update({ moods: [...current, mood] });
    }
  };

  const toggleGi = (gi) => {
    const current = data.giSymptoms || [];
    if (current.includes(gi)) {
      update({ giSymptoms: current.filter(g => g !== gi) });
    } else {
      update({ giSymptoms: [...current, gi] });
    }
  };

  return (
    <div className="space-y-6">
      {/* Flare Day */}
      <div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              if (data.isFlareDay) {
                update({ isFlareDay: false, flareTime: null });
              } else {
                update({ isFlareDay: true, flareTime: data.flareTime || 'MORNING' });
              }
            }}
            className={`flex-1 py-4 rounded-xl font-semibold text-lg transition
              ${data.isFlareDay
                ? 'bg-white/20 text-white ring-2 ring-white/40'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
          >
            {data.isFlareDay ? 'Flare Day' : 'Mark as Flare Day'}
          </button>
        </div>

        {data.isFlareDay && (
          <div className="flex gap-2 mt-3 justify-center">
            <button
              type="button"
              onClick={() => update({ flareTime: 'MORNING' })}
              className={`px-5 py-1.5 rounded-lg text-sm font-medium transition
                ${data.flareTime === 'MORNING'
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              Morning
            </button>
            <button
              type="button"
              onClick={() => update({ flareTime: 'NIGHT' })}
              className={`px-5 py-1.5 rounded-lg text-sm font-medium transition
                ${data.flareTime === 'NIGHT'
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              Night
            </button>
          </div>
        )}
      </div>

      <Slider
        label="Pain Level"
        value={data.painLevel}
        onChange={(v) => update({ painLevel: v })}
        lowLabel="No pain"
        highLabel="Severe"
      />

      {data.painLevel > 1 && (
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">Pain Locations</label>
          <div className="flex flex-wrap gap-2">
            {PAIN_LOCATIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleLocation(key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                  ${(data.painLocations || []).includes(key)
                    ? 'bg-white/20 text-white ring-2 ring-white/30'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Slider
        label="Energy"
        value={data.fatigueLevel}
        onChange={(v) => update({ fatigueLevel: v })}
        lowLabel="Exhausted"
        highLabel="High energy"
      />

      {/* Mood */}
      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">Mood</label>
        <div className="flex flex-wrap gap-2">
          {MOOD_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleMood(key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                ${(data.moods || []).includes(key)
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* GI Symptoms */}
      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">GI Symptoms</label>
        <div className="flex flex-wrap gap-2">
          {GI_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleGi(key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                ${(data.giSymptoms || []).includes(key)
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Cramping */}
      <div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              if (data.crampIntensity != null) {
                update({ crampIntensity: null });
              } else {
                update({ crampIntensity: 5 });
              }
            }}
            className={`flex-1 py-3 rounded-xl font-medium transition
              ${data.crampIntensity != null
                ? 'bg-white/20 text-white ring-2 ring-white/30'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
          >
            {data.crampIntensity != null ? 'Cramping' : 'Experiencing Cramping?'}
          </button>
        </div>

        {data.crampIntensity != null && (
          <div className="mt-3">
            <Slider
              label="Cramp Intensity"
              value={data.crampIntensity}
              onChange={(v) => update({ crampIntensity: v })}
              lowLabel="Mild"
              highLabel="Severe"
            />
          </div>
        )}
      </div>

    </div>
  );
}
